import { getRenderer } from "../component.store";
import { CLASS_CONSTANTS } from "@/utils/constants";
import type { NotificationMessage } from "@/types";
import clsx from "clsx";
import { RenderHtml } from "../RenderHtml";
import { css } from "@linaria/core";
import type { ReactNode } from "react";

const notificationItemClassName = css`
  position: relative;
  isolation: isolate;
  padding: 15px;
  border: 1px solid transparent;
  border-radius: 4px;
  padding-right: 4rem;

  .${CLASS_CONSTANTS.NOTIFICATION.HEADER} {
    color: inherit;
  }

  a {
    font-weight: 700;
  }

  .${CLASS_CONSTANTS.NOTIFICATION.CLOSE} {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 2;
    padding: 0 1.25rem;
    line-height: 2.7rem;
    color: inherit;
  }

  a,
  .alert-link {
    color: inherit !important;
  }

  &.${CLASS_CONSTANTS.NOTIFICATION.TYPE.success} {
    color: #3c763d;
    border-color: #3c763d;
    background-color: #d4edda;

    color: var(--ws-success-text);
    border-color: var(--ws-success-border-color);
    background-color: var(--ws-success-bg);
    hr {
      border-top-color: #3c763d;
      border-top-color: var(--ws-success-border-color);
    }
  }

  &.${CLASS_CONSTANTS.NOTIFICATION.TYPE.info} {
    color: #31708f;
    border-color: #31708f;
    background-color: #d1ecf1;

    color: var(--ws-info-text);
    border-color: var(--ws-info-border-color);
    background-color: var(--ws-info-bg);
    hr {
      border-top-color: #31708f;
      border-top-color: var(--ws-info-border-color);
    }
  }

  &.${CLASS_CONSTANTS.NOTIFICATION.TYPE.warning} {
    color: #8a6d3b;
    border-color: #8a6d3b;
    background-color: #fff3cd;

    color: var(--ws-warning-text);
    border-color: var(--ws-warning-border-color);
    background-color: var(--ws-warning-bg);
    hr {
      border-top-color: #8a6d3b;
      border-top-color: var(--ws-warning-border-color);
    }
  }

  &.${CLASS_CONSTANTS.NOTIFICATION.TYPE.danger} {
    color: #a94442;
    border-color: #a94442;
    background-color: #f8d7da;

    color: var(--ws-danger-text);
    border-color: var(--ws-danger-border-color);
    background-color: var(--ws-danger-bg);
    hr {
      border-top-color: #a94442;
      border-top-color: var(--ws-danger-border-color);
    }
  }
`;

const notificationCloseClassName = css`
  float: right;
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1;
  color: #000;
  text-shadow: 0 1px 0 #fff;
  opacity: 0.5;
  padding: 0;
  background-color: transparent;
  border: 0;
  cursor: pointer;
  outline: none;
  &:before {
    content: "\\00D7";
  }
`;

export type NotificationProps = {
  item: Omit<NotificationMessage, "message"> & { message: ReactNode };
  onClose?: (index: number) => void;
  index: number;
};

const DefaultNotification = ({ item, onClose, index }: NotificationProps) => {
  const { message, level } = item;
  const renderClose = () =>
    onClose ? (
      <button
        onClick={() => onClose(index)}
        className={clsx(
          CLASS_CONSTANTS.NOTIFICATION.CLOSE,
          notificationCloseClassName,
        )}
      ></button>
    ) : null;

  return (
    <li
      role={level === "danger" ? "alert" : "status"}
      className={clsx(
        CLASS_CONSTANTS.NOTIFICATION.ROOT,
        CLASS_CONSTANTS.NOTIFICATION.TYPE[level],
        notificationItemClassName,
      )}
    >
      <RenderHtml>{message}</RenderHtml>
      {renderClose()}
    </li>
  );
};

export const Notification = getRenderer("Notification", DefaultNotification);
